<template>
  <div class="assignment-details">
    <!-- Prompt teacher to link assignment data when missing or unconfirmed -->
    <div v-if="needsLinking" class="linker-wrapper">
      <AssignmentDataLinker
        :learning-unit-id="assignmentData.learningUnit?.id || ''"
        :existing-assignment-id="assignmentData.assignmentDetails?.id"
        :existing-data="assignmentData.assignmentDetails"
        @confirmed="onAssignmentConfirmed"
      />
    </div>

    <div class="assignment-content">
      <!-- Left side: Text content -->
      <div class="text-content">
        <div class="details-section" v-html="assignmentText"></div>

        <div class="details-section">
          <h2>Arviointiperusteet</h2>
          <ul
            v-if="
              (assignmentData.assignmentDetails?.arviointiperusteet?.length ||
                0) > 0
            "
          >
            <li
              v-for="(criterion, index) in assignmentData.assignmentDetails
                ?.arviointiperusteet"
              :key="index"
            >
              {{ criterion }}
            </li>
          </ul>
          <p v-else>Arviointiperusteita ei löytynyt.</p>
        </div>

        <div class="details-section">
          <div class="gallery-header">
            <h2>Kotitehtävägalleria</h2>
            <button
              v-if="assignmentData.assignmentDetails?.id"
              @click="enableGalleryEdit"
              class="edit-icon-button header-edit"
              title="Muokkaa linkkiä"
            >
              <BaseIcon name="edit" :size="16" />
            </button>
          </div>

          <!-- Editing mode -->
          <div v-if="isEditingGalleryLink" class="gallery-edit-mode">
            <div class="edit-form">
              <label for="gallery-link-input"
                >Google Drive -kansion linkki:</label
              >
              <input
                id="gallery-link-input"
                v-model="editingGalleryLink"
                type="url"
                placeholder="https://drive.google.com/drive/folders/..."
                class="gallery-link-input"
              />
              <div class="edit-buttons">
                <button
                  @click="saveGalleryLink"
                  :disabled="isSavingGalleryLink"
                  class="save-button"
                >
                  {{ isSavingGalleryLink ? "Tallennetaan..." : "Tallenna" }}
                </button>
                <button @click="cancelGalleryEdit" class="cancel-button">
                  Peruuta
                </button>
              </div>
            </div>
            <div class="setup-help">
              <p>
                <a
                  href="https://drive.google.com/drive/folders/1uDd-ZX2SCEJm9HacuL4CrBnxJL_NxGs_"
                  target="_blank"
                  class="drive-link"
                >
                  Siirry Google Driveen
                </a>
                ja luo tai etsi tehtäväkohtainen kansio. Jaa kansio julkisesti
                ja kopioi linkki.
              </p>
            </div>
          </div>

          <!-- Display mode with gallery link -->
          <div
            v-else-if="assignmentData.assignmentDetails?.peerGalleryDriveLink"
            class="gallery-display-mode"
          >
            <div class="gallery-actions">
              <a
                :href="assignmentData.assignmentDetails.peerGalleryDriveLink"
                target="_blank"
                class="drive-link-button"
              >
                <BaseIcon
                  name="google-drive"
                  :size="16"
                  class-name="drive-icon"
                />
                <span>Tarkastele Drivessä</span>
                <svg
                  class="external-link-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"
                  />
                </svg>
              </a>
              <button
                @click="openPeerGallerySection"
                class="gallery-embed-button primary"
              >
                <span v-if="isPeerGalleryOpen" class="action">Sulje</span>
                <span v-else class="action">Avaa</span>
                galleria
              </button>
            </div>
          </div>

          <!-- No gallery set -->
          <div v-else class="no-gallery-notice">
            <p>Kotitehtävägalleriaa ei ole asetettu.</p>
            <div class="setup-instructions">
              <p>
                <a
                  href="https://drive.google.com/drive/folders/1uDd-ZX2SCEJm9HacuL4CrBnxJL_NxGs_"
                  target="_blank"
                  class="drive-link"
                >
                  Siirry Google Driveen
                </a>
                ja luo tai etsi tehtäväkohtainen kansio. Jaa kansio julkisesti
                ja kopioi linkki.
              </p>
              <button @click="enableGalleryEdit" class="edit-button">
                Aseta galleria-linkki
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side: Assignment images -->
      <div class="assignment-images" v-if="assignmentImages.length > 0">
        <div class="images-grid">
          <img
            v-for="(imageUrl, index) in assignmentImages"
            :key="index"
            :src="imageUrl"
            alt="Tehtävän kuva"
            class="assignment-image"
            @click="openAssignmentImage(imageUrl)"
          />
        </div>
      </div>
    </div>

    <div class="details-section">
      <h2>
        Palautus: {{ assignmentData.userName || "Tuntematon opiskelija" }}
      </h2>
      <div v-if="!isGalleryOpen && allImagesCount > 0" class="images">
        <img
          v-for="image in allImages"
          :key="image.url"
          :src="image.url"
          alt="Kuva"
          class="default-image"
          @click="openImage(image)"
        />
      </div>
      <div
        v-else-if="isGalleryOpen && allImagesCount > 0"
        class="images thumbnails"
      >
        <img
          v-for="image in allImages"
          :key="image.url"
          :src="image.url"
          alt="Kuva"
          class="thumbnail-image"
          @click="closeGallery"
        />
      </div>
      <p v-else>Ei kuvia.</p>
    </div>

    <div
      v-if="!!assignmentData.answers?.[0]?.description"
      class="details-section"
    >
      <p>
        {{ assignmentData.answers?.[0]?.description || "Ei saatetekstiä." }}
      </p>
    </div>

    <div class="details-section">
      <h2 class="teacher-notes-header">
        Opettajan huomiot
        <span
          v-if="isGradingStarted"
          class="lock-icon"
          title="Huomiot lukittu arvioinnin ajaksi"
          >🔒</span
        >
      </h2>
      <div class="textarea-container" :class="{ frozen: isGradingStarted }">
        <textarea
          v-model="teacherNotesText"
          :readonly="isGradingStarted"
          placeholder=" - Ensimmäinen havainto
 - Toinen tärkeä asia
 - Kolmas huomio"
        ></textarea>
      </div>
      <button
        v-if="!isGradingStarted"
        class="start-grading-button"
        :class="{ disabled: !canStartGrading }"
        :disabled="!canStartGrading"
        @click="startGrading"
      >
        Aloita arviointi
      </button>
      <button v-else class="reset-grading-button" @click="resetGrading">
        Aloita arviointi alusta
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import AssignmentDataLinker from "@/components/AssignmentDataLinker.vue";
import BaseIcon from "@/components/BaseIcon.vue";
import { marked } from "marked";
import { computed, ref, watch } from "vue";
import { apiUrl } from "@/config/api";

interface ImageDownload {
  url: string;
  title: string;
  fileType: string;
}

interface AssignmentDetails {
  id?: number;
  isConfirmed?: boolean;
  otsikko?: string;
  alaotsikko?: string | null;
  lukukausi?: string;
  tehtävänanto?: string;
  arviointiperusteet?: string[];
  kuvat?: string[];
  peerGalleryDriveLink?: string | null;
}

interface LearningUnit {
  title?: string;
}

interface Section {
  title?: string;
  description?: string;
}

interface Answer {
  description?: string;
  answerData?: {
    downloads?: ImageDownload[];
  };
}

interface AssignmentData {
  userName?: string;
  assignmentDetails?: AssignmentDetails;
  learningUnit?: LearningUnit & { id?: string };
  section?: Section;
  answers?: Answer[];
}

const props = defineProps<{
  assignmentData: AssignmentData;
  isGalleryOpen?: boolean;
  teacherNotes?: string;
  isGradingStarted?: boolean;
  isPeerGalleryOpen: boolean;
}>();

const assignmentText = computed(() => {
  if (!props.assignmentData) {
    return "Tehtävänantoa ei löytynyt.";
  }

  const assignment_text =
    props.assignmentData.assignmentDetails?.tehtävänanto ||
    props.assignmentData.section?.description ||
    "Tehtävänantoa ei löytynyt.";

  const text = `
  # ${props.assignmentData.learningUnit?.title || "Tehtävän nimeä ei löytynyt"}

  ## ${props.assignmentData.section?.title || "Otsikkoa ei löytynyt"}

  ${assignment_text}
  `;

  return marked(text);
});

const emit = defineEmits([
  "open-image-gallery",
  "close-image-gallery",
  "open-assignment-gallery",
  "update-teacher-notes",
  "start-grading",
  "reset-grading",
  "toggle-embedded-gallery",
  "enable-gallery-edit",
  "update-gallery-link",
  "toggle-peer-gallery",
  "assignment-confirmed",
]);

const needsLinking = computed(
  () =>
    !props.assignmentData.assignmentDetails ||
    !props.assignmentData.assignmentDetails.isConfirmed,
);

const onAssignmentConfirmed = (details: AssignmentDetails) => {
  emit("assignment-confirmed", details);
};

const isEditingGalleryLink = ref(false);
const editingGalleryLink = ref("");
const isSavingGalleryLink = ref(false);

const teacherNotesText = ref(props.teacherNotes || "");

// Watch for external changes to teacherNotes prop
watch(
  () => props.teacherNotes,
  (newValue) => {
    teacherNotesText.value = newValue || "";
  },
);

// Emit changes when user types
watch(teacherNotesText, (newValue) => {
  emit("update-teacher-notes", newValue);
});

// Check if teacher notes meet minimum requirements for grading
const canStartGrading = computed(() => {
  const text = teacherNotesText.value.trim();
  if (!text) return false;

  // Check character count (at least 100 characters)
  if (text.length >= 100) return true;

  // Check line count (at least 3 lines)
  const lines = text.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length >= 3) return true;

  // Check word count (at least 15 words)
  const words = text.split(/\s+/).filter((word) => word.length > 0);
  if (words.length >= 15) return true;

  return false;
});

const startGrading = () => {
  if (canStartGrading.value) {
    emit("start-grading", teacherNotesText.value);
  }
};

const resetGrading = () => {
  emit("reset-grading");
};

const allImagesCount = computed(() => {
  return props.assignmentData.answers?.[0]?.answerData?.downloads?.length || 0;
});

const allImages = computed(() => {
  return props.assignmentData.answers?.[0]?.answerData?.downloads || [];
});

const assignmentImages = computed(() => {
  return props.assignmentData.assignmentDetails?.kuvat || [];
});

const openImage = (image: ImageDownload) => {
  emit("open-image-gallery", image);
};

const openAssignmentImage = (imageUrl: string) => {
  // Create a mock image object for the gallery
  const imageObject = {
    url: imageUrl,
    title: "Tehtävän kuva",
    fileType: "image/jpeg",
  };

  // Create an array of all assignment images for the gallery
  const allAssignmentImages = assignmentImages.value.map((url, index) => ({
    url: url,
    title: `Tehtävän kuva ${index + 1}`,
    fileType: "image/jpeg",
  }));

  // Emit to separate assignment gallery
  emit("open-assignment-gallery", imageObject, allAssignmentImages);
};

const closeGallery = () => {
  emit("close-image-gallery");
};

const openPeerGallerySection = () => {
  const driveUrl = props.assignmentData.assignmentDetails?.peerGalleryDriveLink;
  if (driveUrl) {
    const embeddableUrl = getEmbeddableGalleryUrl(driveUrl);
    emit("toggle-peer-gallery", embeddableUrl);
  }
};

const getEmbeddableGalleryUrl = (driveUrl: string): string => {
  // Convert Google Drive sharing URL to embeddable format
  // From: https://drive.google.com/drive/folders/FOLDER_ID?usp=sharing
  // To: https://drive.google.com/embeddedfolderview?id=FOLDER_ID#grid

  try {
    const url = new URL(driveUrl);
    if (
      url.hostname === "drive.google.com" &&
      url.pathname.includes("/folders/")
    ) {
      const folderId = url.pathname.split("/folders/")[1]?.split("/")[0];
      if (folderId) {
        return `https://drive.google.com/embeddedfolderview?id=${folderId}#grid`;
      }
    }
  } catch {
    console.warn("Invalid drive URL:", driveUrl);
  }

  // Fallback to original URL if conversion fails
  return driveUrl;
};

const enableGalleryEdit = () => {
  editingGalleryLink.value =
    props.assignmentData.assignmentDetails?.peerGalleryDriveLink || "";
  isEditingGalleryLink.value = true;
};

const cancelGalleryEdit = () => {
  isEditingGalleryLink.value = false;
  editingGalleryLink.value = "";
};

const saveGalleryLink = async () => {
  if (!props.assignmentData.assignmentDetails?.id) {
    console.error("No assignment ID available");
    return;
  }

  isSavingGalleryLink.value = true;

  try {
    const response = await fetch(
      apiUrl(`/assignments/${props.assignmentData.assignmentDetails.id}`),
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          peer_gallery_drive_link: editingGalleryLink.value.trim() || null,
        }),
      },
    );

    if (!response.ok) {
      const errorData = (await response.json()) as { error?: string };
      throw new Error(errorData.error || "Failed to update assignment");
    }

    const result = (await response.json()) as {
      assignment: { peer_gallery_drive_link: string | null };
    };
    console.log("✅ Gallery link updated:", result);

    // Emit the update instead of mutating props
    emit("update-gallery-link", editingGalleryLink.value.trim() || null);

    isEditingGalleryLink.value = false;
    editingGalleryLink.value = "";
  } catch (error) {
    console.error("Error updating gallery link:", error);
    alert(
      "Virhe galleria-linkin tallentamisessa: " +
        (error instanceof Error ? error.message : "Tuntematon virhe"),
    );
  } finally {
    isSavingGalleryLink.value = false;
  }
};
</script>

<style scoped>
.assignment-details {
  padding: 0 1rem 1rem 1rem;
}

.linker-wrapper {
  margin-bottom: 1.5rem;
}

.assignment-content {
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
}

.text-content {
  flex: 1;
  min-width: 0; /* Allows flex child to shrink below its content size */
}

.assignment-images {
  flex: 0 0 300px; /* Fixed width for images column */
  /* background-color: #f8f9fa; */
  border-radius: 8px;
  padding: 1rem;
  /* border: 1px solid #e9ecef; */
}

.assignment-images h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
}

.images-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.assignment-image {
  width: 100%;
  height: auto;
  border-radius: 4px;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  border: 1px solid #dee2e6;
}

.assignment-image:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .assignment-content {
    flex-direction: column;
  }

  .assignment-images {
    flex: none;
    width: 100%;
  }

  .images-grid {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .assignment-image {
    width: calc(50% - 0.5rem);
  }
}

.details-section {
  margin-bottom: 1.5rem;
}
li {
  margin-bottom: 0.5rem;
}
.images {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.default-image {
  max-width: 300px;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.default-image:hover {
  transform: scale(1.05);
}

.thumbnail-image {
  max-width: 75px;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
  opacity: 0.8;
}

.thumbnail-image:hover {
  transform: scale(1.05);
  opacity: 1;
}
textarea {
  width: 100%;
  min-height: 150px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0.5rem;
  font-family: inherit;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.teacher-notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.lock-icon {
  font-size: 0.9rem;
  opacity: 0.7;
  color: #6c757d;
}

.textarea-container {
  position: relative;
  transition: opacity 0.3s ease;
}

.textarea-container.frozen {
  opacity: 0.7;
}

.textarea-container.frozen:hover {
  opacity: 0.5;
}

textarea:read-only {
  background-color: #f8f9fa;
  cursor: not-allowed;
  border-color: #dee2e6;
  transition: all 0.3s ease;
}

textarea:read-only:hover {
  cursor: not-allowed;
  background-color: #e9ecef;
}

.start-grading-button {
  padding: var(--space-md) var(--space-lg);
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-white);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 700;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease-in-out;
  width: 100%;
}

.start-grading-button:hover:not(.disabled) {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.start-grading-button.disabled {
  background-color: var(--color-bg-tertiary);
  color: var(--color-text-light);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.start-grading-button:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.reset-grading-button {
  padding: var(--space-md) var(--space-lg);
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-white);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 700;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease-in-out;
  width: 100%;
}

.reset-grading-button:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.reset-grading-button:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* The markddown */
:deep(h1),
:deep(h2),
:deep(h3),
:deep(h4),
:deep(h5),
:deep(h6) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}
:deep(p) {
  margin: 0;
  margin-top: 0.75rem;
}

:deep(strong) {
  font-weight: 600;
}

:deep(em) {
  font-style: italic;
}

:deep(ul),
:deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(li) {
  margin-bottom: 0.25rem;
}

:deep(blockquote) {
  border-left: 4px solid #e0e0e0;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #666;
}

:deep(code) {
  background-color: #f5f5f5;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
}

/* Peer gallery styles */
.gallery-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.gallery-header h2 {
  margin: 0;
}

.edit-icon-button.header-edit {
  padding: 0.375rem;
  min-width: 28px;
  min-height: 28px;
  border: none;
  color: #6c757d;
}

.edit-icon-button.header-edit:hover {
  background: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
}

.drive-link-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  text-decoration: none;
  font-weight: 500;
  margin-right: 1rem;
  padding: 0.5rem 1rem;
  border: 1px solid #6c757d;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.drive-link-button:hover {
  background-color: #6c757d;
  color: white;
}

.external-link-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.drive-icon {
  flex-shrink: 0;
}

.gallery-embed-button {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.gallery-embed-button.primary {
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: white;
}

.gallery-embed-button.primary:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
}

.gallery-embed-button:not(.primary) {
  border: 1px solid #6c757d;
  background: white;
  color: #6c757d;
}

.gallery-embed-button:not(.primary):hover {
  background: #6c757d;
  color: white;
}

.no-gallery-notice {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
}

.setup-instructions {
  margin-top: 0.5rem;
}

.drive-link {
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 600;
}

.drive-link:hover {
  text-decoration: underline;
}

.edit-button {
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  transition: all 0.2s ease;
}

.edit-button:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
}

/* Gallery editing styles */
.gallery-edit-mode {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
}

.edit-form {
  margin-bottom: 1rem;
}

.edit-form label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
}

.gallery-link-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.gallery-link-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.edit-buttons {
  display: flex;
  gap: 0.5rem;
}

.save-button {
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.save-button:hover:not(:disabled) {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
}

.save-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-button {
  padding: 0.5rem 1rem;
  border: 1px solid #6c757d;
  background: white;
  color: #6c757d;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background: #6c757d;
  color: white;
}

.setup-help {
  padding: 0.75rem;
  background-color: #e9ecef;
  border-radius: 4px;
  font-size: 0.9rem;
}

.gallery-display-mode .gallery-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.edit-icon-button {
  padding: 0.5rem;
  border: 1px solid #6c757d;
  background: white;
  color: #6c757d;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
}

.edit-icon-button:hover {
  background: #6c757d;
  color: white;
}
</style>
